<template>
  <div class="resale-tax-container">
    <!-- Header Section -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 mb-2">
        Resale Tax Documents
      </h1>
      <p class="text-gray-600 text-sm">
        Manage and validate resale tax documents uploaded by customers.
      </p>
    </div>

    <!-- Filters Card -->
    <div class="mb-6 bg-white shadow-sm border border-gray-200 rounded-lg p-4">
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-base font-semibold text-gray-900">Filters</h3>
        <div class="flex items-center space-x-2">
          <span class="text-xs text-gray-500">
            <i class="fas fa-filter mr-1"></i>
            Filter documents
          </span>
        </div>
      </div>
      <div class="flex flex-row gap-3 flex-wrap">
        <button
          :class="{
            'bg-blue-600 text-white shadow-md':
              filters.pendingValidation === true,
            'bg-gray-100 text-gray-700 hover:bg-gray-200':
              filters.pendingValidation !== true
          }"
          class="px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          @click="updateFilter('pendingValidation')"
        >
          <i class="fas fa-clock mr-2"></i>
          Pending Validation
        </button>
        <button
          :class="{
            'bg-red-600 text-white shadow-md': filters.rejected === true,
            'bg-gray-100 text-gray-700 hover:bg-gray-200':
              filters.rejected !== true
          }"
          class="px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500"
          @click="updateFilter('rejected')"
        >
          <i class="fas fa-times-circle mr-2"></i>
          Rejected
        </button>
        <button
          :class="{
            'bg-gray-500 text-white shadow-md': filters.error === true,
            'bg-gray-100 text-gray-700 hover:bg-gray-200':
              filters.error !== true
          }"
          class="px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
          @click="updateFilter('error')"
        >
          <i class="fas fa-exclamation-triangle mr-2"></i>
          Error
        </button>
        <button
          :class="{
            'bg-green-600 text-white shadow-md': filters.validated === true,
            'bg-gray-100 text-gray-700 hover:bg-gray-200':
              filters.validated !== true
          }"
          class="px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500"
          @click="updateFilter('validated')"
        >
          <i class="fas fa-check-circle mr-2"></i>
          Validated
        </button>
        <button
          :class="{
            'bg-gray-700 text-white shadow-md': filters.deleted === true,
            'bg-gray-100 text-gray-700 hover:bg-gray-200':
              filters.deleted !== true
          }"
          class="px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          @click="updateFilter('deleted')"
        >
          <i class="fas fa-trash mr-2"></i>
          Deleted
        </button>
        <button
          :disabled="bulkValidating"
          class="px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-purple-600 text-white shadow-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          @click="validateAllPendingAI"
        >
          <i
            :class="bulkValidating ? 'fa-spinner fa-spin' : 'fa-robot'"
            class="fas mr-2"
          ></i>
          {{ bulkValidating ? "Validating…" : "AI Validate All Pending" }}
        </button>
      </div>

      <!-- Validated source sub-filter -->
      <div
        v-if="filters.validated === true"
        class="flex items-center gap-2 mt-3 pl-1"
      >
        <span class="text-xs text-gray-500">Validated by:</span>
        <button
          v-for="opt in validatedSourceOptions"
          :key="opt.value"
          :class="{
            'bg-green-600 text-white': filters.validatedSource === opt.value,
            'bg-gray-100 text-gray-700 hover:bg-gray-200':
              filters.validatedSource !== opt.value
          }"
          class="px-3 py-1 rounded-full text-xs font-medium transition-colors focus:outline-none"
          @click="setValidatedSource(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>
    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-12">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"
      ></div>
    </div>

    <!-- Documents Table -->
    <div
      v-else
      class="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden"
    >
      <div class="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 class="text-base font-semibold text-gray-900">
          Resale Tax Documents ({{ documents.length }})
        </h3>
      </div>

      <div class="table-wrapper">
        <table class="w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                Customer
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                From Date
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                To Date
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                ID
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                Email
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                Uploaded By
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                Uploaded At
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                Status
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                Validated
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                AI Validation
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(document, index) in documents"
              :key="document.id"
              :class="{
                'bg-red-50': document.deletedAt,
                'bg-gray-50': index % 2 === 1 && !document.deletedAt
              }"
              class="hover:bg-gray-100 transition-colors"
            >
              <td
                class="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900"
              >
                {{ document.customerName || "N/A" }}
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                {{ document.fromDate | dateWithHours }}
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                {{ document.toDate | dateWithHours }}
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                <span class="font-mono bg-gray-100 px-2 py-1 rounded text-xs">
                  {{ document.infoautoId }}
                </span>
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                <a
                  :href="`mailto:${document.customerEmail}`"
                  class="text-blue-600 hover:text-blue-800"
                >
                  {{ document.customerEmail }}
                </a>
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                <span v-if="document.uploaderName" class="text-gray-900">
                  {{ document.uploaderName }}
                </span>
                <span v-else class="text-red-600 font-semibold">
                  user
                </span>
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                {{ document.createdAt | dateWithHours }}
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-xs">
                <span
                  v-if="document.deletedAt"
                  class="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800"
                >
                  Deleted
                </span>
                <span
                  v-else-if="isRejected(document)"
                  class="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800"
                >
                  Rejected
                </span>
                <span
                  v-else
                  class="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800"
                >
                  Active
                </span>
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                <div v-if="document.validatedBy" class="flex flex-col">
                  <span class="font-medium text-green-700 text-xs">
                    {{ document.validatorName }}
                  </span>
                  <span class="text-xs text-gray-500">
                    {{ document.validatedAt | dateWithHours }}
                  </span>
                </div>
                <div v-else-if="document.validatedAt" class="flex flex-col">
                  <span class="font-medium text-purple-700 text-xs">
                    <i class="fas fa-robot mr-1"></i>AI
                  </span>
                  <span class="text-xs text-gray-500">
                    {{ document.validatedAt | dateWithHours }}
                  </span>
                </div>
                <span
                  v-else-if="isRejected(document)"
                  class="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800"
                >
                  Rejected
                </span>
                <span v-else class="text-yellow-600 font-medium text-xs">
                  Pending
                </span>
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                <span
                  :class="aiStatusClass(document.aiStatus)"
                  class="px-2 py-1 text-xs font-semibold rounded-full"
                >
                  {{ aiStatusLabel(document.aiStatus) }}
                </span>
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-xs font-medium">
                <button
                  @click="openFeedback(document)"
                  class="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors"
                  title="Open document"
                >
                  <i class="fas fa-folder-open mr-1"></i>
                  Open
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div v-if="documents.length === 0" class="text-center py-12">
          <div class="text-gray-400 text-lg mb-2">📄</div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            No documents found
          </h3>
          <p class="text-gray-500">
            There are no resale tax documents to display.
          </p>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="mt-4 bg-white border border-gray-200 rounded-lg p-4">
      <div class="flex items-center justify-between flex-wrap gap-3">
        <div class="text-sm text-gray-700">
          Showing {{ filters.limitPerPage * (filters.page - 1) + 1 }} to
          {{ toQuantity() }} of {{ totalItems }} results
        </div>

        <div class="flex items-center space-x-4">
          <div class="flex items-center space-x-2">
            <label class="text-sm text-gray-700">Per page:</label>
            <select
              v-model="filters.limitPerPage"
              @change="filters.page = 1"
              class="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="500">500</option>
            </select>
          </div>

          <div class="flex items-center space-x-2">
            <button
              :disabled="filters.page <= 1"
              :class="{
                'bg-gray-100 text-gray-400 cursor-not-allowed':
                  filters.page <= 1,
                'bg-white text-gray-700 hover:bg-gray-50 border-gray-300':
                  filters.page > 1
              }"
              class="px-4 py-2 border rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              @click="previous()"
            >
              <i class="fas fa-chevron-left mr-2"></i>
              Previous
            </button>

            <span class="px-4 py-2 text-sm text-gray-700">
              Page {{ filters.page }} of {{ totalPages }}
            </span>

            <button
              :disabled="filters.page >= totalPages"
              :class="{
                'bg-gray-100 text-gray-400 cursor-not-allowed':
                  filters.page >= totalPages,
                'bg-white text-gray-700 hover:bg-gray-50 border-gray-300':
                  filters.page < totalPages
              }"
              class="px-4 py-2 border rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              @click="next()"
            >
              Next
              <i class="fas fa-chevron-right ml-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk AI Validation Result Modal -->
    <BaseModal
      v-if="bulkResult"
      id="resale-tax-bulk-modal"
      :value="true"
      @input="closeBulkResult"
    >
      <div class="flex flex-col gap-4">
        <h2 class="text-xl font-bold text-gray-900">
          AI Validation — Bulk Result
        </h2>

        <div class="grid grid-cols-2 gap-3">
          <div class="rounded-lg border border-gray-200 p-3 text-center">
            <div class="text-2xl font-bold text-gray-900">
              {{ bulkResult.summary.processed }}
            </div>
            <div class="text-xs text-gray-500 uppercase tracking-wider">
              Processed
            </div>
          </div>
          <div
            class="rounded-lg border border-green-200 bg-green-50 p-3 text-center"
          >
            <div class="text-2xl font-bold text-green-700">
              {{ bulkResult.summary.passed }}
            </div>
            <div class="text-xs text-green-700 uppercase tracking-wider">
              Passed
            </div>
          </div>
          <div
            class="rounded-lg border border-red-200 bg-red-50 p-3 text-center"
          >
            <div class="text-2xl font-bold text-red-700">
              {{ bulkResult.summary.failed }}
            </div>
            <div class="text-xs text-red-700 uppercase tracking-wider">
              Rejected
            </div>
          </div>
          <div
            class="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center"
          >
            <div class="text-2xl font-bold text-gray-700">
              {{ bulkResult.summary.errors }}
            </div>
            <div class="text-xs text-gray-500 uppercase tracking-wider">
              Errors
            </div>
          </div>
        </div>

        <div
          v-if="bulkResult.rateLimited"
          class="text-sm text-orange-800 bg-orange-50 border border-orange-200 rounded-lg p-3"
        >
          <i class="fas fa-exclamation-triangle mr-1"></i>
          Stopped early due to the rate limit. Wait a minute and run it again.
        </div>
        <div
          v-else-if="bulkResult.remaining > 0"
          class="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3"
        >
          <i class="fas fa-layer-group mr-1"></i>
          {{ bulkResult.remaining }} document(s) left to process — run "AI
          Validate All Pending" again to continue.
        </div>
        <div
          v-else
          class="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3"
        >
          <i class="fas fa-check mr-1"></i>
          All eligible pending documents have been processed.
        </div>

        <div v-if="bulkResult.errors.length">
          <h3 class="text-base font-semibold text-gray-900 mb-1">
            Errors ({{ bulkResult.errors.length }})
          </h3>
          <ul
            class="flex flex-col gap-2 max-h-60 overflow-y-auto text-sm text-gray-700"
          >
            <li
              v-for="err in bulkResult.errors"
              :key="err.id"
              class="bg-gray-50 border border-gray-200 rounded-lg p-2"
            >
              <span class="font-medium">#{{ err.id }}</span>
              <span v-if="err.file_name" class="text-gray-500">
                · {{ err.file_name }}</span
              >
              <div class="text-gray-600">{{ err.message }}</div>
            </li>
          </ul>
        </div>

        <div class="flex justify-end">
          <button
            @click="closeBulkResult"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </BaseModal>

    <!-- AI Feedback Modal -->
    <BaseModal
      v-if="selectedDoc"
      id="resale-tax-feedback-modal"
      :value="true"
      @input="closeFeedback"
    >
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between">
          <h2 class="text-xl font-bold text-gray-900">Resale Tax Document</h2>
          <span
            :class="aiStatusClass(selectedDoc.aiStatus)"
            class="px-3 py-1 text-sm font-semibold rounded-full"
          >
            {{ aiStatusLabel(selectedDoc.aiStatus) }}
          </span>
        </div>

        <!-- Actions -->
        <div class="flex flex-wrap items-center gap-2">
          <a
            :href="selectedDoc.url"
            target="_blank"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
          >
            <i class="fas fa-external-link-alt mr-2"></i>
            Open in new tab
          </a>
          <button
            v-if="selectedDoc.infoautoId"
            @click="openCustomer(selectedDoc)"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-100 rounded-lg hover:bg-indigo-200 transition-colors"
          >
            <i class="fas fa-user mr-2"></i>
            View customer
          </button>
          <button
            v-if="!selectedDoc.validatedBy && !selectedDoc.deletedAt"
            :disabled="aiValidatingId === selectedDoc.id"
            @click="validateDocumentAI(selectedDoc.id)"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-purple-600 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i
              :class="
                aiValidatingId === selectedDoc.id
                  ? 'fa-spinner fa-spin'
                  : 'fa-robot'
              "
              class="fas mr-2"
            ></i>
            AI Validate
          </button>
          <button
            v-if="!selectedDoc.validatedBy && !selectedDoc.deletedAt"
            @click="validateDocument(selectedDoc.id)"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-green-600 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
          >
            <i class="fas fa-check mr-2"></i>
            Validate
          </button>
          <button
            v-if="canDelete && !selectedDoc.deletedAt"
            @click="deleteResaleTax(selectedDoc.id)"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
          >
            <i class="fas fa-trash mr-2"></i>
            Delete
          </button>
        </div>

        <div class="flex flex-col gap-1 text-base text-gray-700">
          <div>
            <span class="font-medium text-gray-500">Customer:</span>
            {{ selectedDoc.customerName || "N/A" }}
          </div>
          <div class="break-all">
            <span class="font-medium text-gray-500">File:</span>
            {{ selectedDoc.fileName }}
          </div>
          <div>
            <span class="font-medium text-gray-500">Uploaded by:</span>
            {{ selectedDoc.uploaderName || "N/A" }}
          </div>
          <div>
            <span class="font-medium text-gray-500">Uploaded at:</span>
            {{ selectedDoc.createdAt | dateWithHours }}
          </div>
          <div>
            <span class="font-medium text-gray-500">Valid:</span>
            {{ selectedDoc.fromDate | dateWithHours }} —
            {{ selectedDoc.toDate | dateWithHours }}
          </div>
        </div>

        <div v-if="selectedDoc.aiFeedback || selectedDoc.rejectedMessage">
          <h3 class="text-base font-semibold text-gray-900 mb-1">Feedback</h3>
          <p
            class="text-base text-gray-800 whitespace-pre-line leading-relaxed bg-gray-50 border border-gray-200 rounded-lg p-3"
          >
            {{ selectedDoc.aiFeedback || selectedDoc.rejectedMessage }}
          </p>
        </div>

        <div v-if="selectedDoc.deletedReason">
          <h3 class="text-base font-semibold text-gray-900 mb-1">
            Deleted reason
          </h3>
          <p
            class="text-base text-red-800 whitespace-pre-line leading-relaxed bg-red-50 border border-red-200 rounded-lg p-3"
          >
            {{ selectedDoc.deletedReason }}
          </p>
        </div>

        <div>
          <h3 class="text-base font-semibold text-gray-900 mb-1">Document</h3>
          <img
            v-if="isImageDoc(selectedDoc.fileName)"
            :src="selectedDoc.url"
            :alt="selectedDoc.fileName"
            class="w-full rounded-lg border border-gray-200"
          />
          <iframe
            v-else
            :src="selectedDoc.url"
            :title="selectedDoc.fileName"
            class="w-full rounded-lg border border-gray-200"
            style="height: 60vh"
          ></iframe>
        </div>

        <div class="flex justify-end">
          <button
            @click="closeFeedback"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </BaseModal>
  </div>
</template>
<script>
import {
  getResaleTaxDocuments,
  deleteResaleTax as deleteResaleTaxApi,
  validateResaleTax as validateResaleTaxApi,
  validateResaleTaxAI as validateResaleTaxAIApi,
  validateAllPendingResaleTaxAI as validateAllPendingResaleTaxAIApi
} from "@/api/store/CustomersService.js";
export default {
  name: "ResaleTax",
  data() {
    return {
      publicPath: process.env.VUE_APP_IMG_BASE_URL,
      documents: [],
      totalItems: 0,
      totalPages: 0,
      loading: true,
      bulkValidating: false,
      aiValidatingId: null,
      selectedDoc: null,
      bulkResult: null,
      validatedSourceOptions: [
        { value: "all", label: "All" },
        { value: "ai", label: "AI" },
        { value: "human", label: "Human" }
      ],
      filters: {
        fromDate: "",
        toDate: "",
        page: 1,
        limitPerPage: 100,
        orderCol: "created_at",
        orderDir: "desc",
        search: "",
        pendingValidation: true,
        rejected: null,
        error: null,
        deleted: null,
        validated: null,
        validatedSource: "all"
      }
    };
  },
  mounted() {
    this.loadDocuments();
  },
  computed: {
    currentUser() {
      return this.$store.getters["auth/currentUser"];
    },
    canDelete() {
      return this.$store.getters["auth/userHasPermission"](
        "resale_tax_management"
      );
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        if (!this.loading) {
          this.loadDocuments();
        }
      }
    }
  },
  methods: {
    updateFilter(filter) {
      // These status filters are mutually exclusive; turn the others off.
      const exclusive = [
        "pendingValidation",
        "rejected",
        "error",
        "deleted",
        "validated"
      ];
      if (!exclusive.includes(filter)) {
        return;
      }
      if (this.filters[filter]) {
        this.filters[filter] = null;
      } else {
        exclusive.forEach(f => {
          this.filters[f] = f === filter ? true : null;
        });
      }
      // Reset the validated sub-filter whenever leaving the validated view.
      if (this.filters.validated !== true) {
        this.filters.validatedSource = "all";
      }
      this.filters.page = 1;
    },
    setValidatedSource(source) {
      this.filters.validatedSource = source;
      this.filters.page = 1;
    },
    loadDocuments() {
      this.loading = true;
      getResaleTaxDocuments(this.filters)
        .then(res => {
          this.documents = res.documents;
          this.totalItems = res.totalItems;
          this.totalPages = res.totalPages;
        })
        .catch(() => {
          this.$toast.open({
            message: "Error getting documents",
            type: "Error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    previous() {
      if (this.filters.page > 1) {
        this.filters.page = this.filters.page - 1;
      }
    },
    next() {
      if (this.totalItems >= this.filters.limitPerPage * this.filters.page) {
        this.filters.page = this.filters.page + 1;
      }
    },
    toQuantity() {
      if (this.qtyPerPage * this.page > this.totalItems) {
        return this.documents.length;
      } else {
        return (
          this.filters.limitPerPage * (this.filters.page - 1) +
          this.documents.length
        );
      }
    },
    changeArrowDir(headerValue) {
      this.filters.page = 1;
      if (headerValue == this.filters.orderCol) {
        this.filters.orderDir =
          this.filters.orderDir == "desc" ? "asc" : "desc";
      } else {
        this.filters.orderCol = headerValue;
        this.filters.orderDir = "desc";
      }
    },
    deleteResaleTax(id) {
      if (
        confirm("Are you sure you want to delete this resale tax document?")
      ) {
        const reason = window.prompt("Reason for deleting (optional):", "");
        deleteResaleTaxApi(id, reason || undefined)
          .then(response => {
            // Check if the response indicates success
            if (
              response.status === "success" ||
              response.message === "success"
            ) {
              // Update the local document with deletedAt timestamp
              const index = this.documents.findIndex(doc => doc.id === id);
              if (index !== -1) {
                this.$set(this.documents, index, {
                  ...this.documents[index],
                  deletedAt: new Date().toISOString()
                });
              }

              this.$toast.open({
                message: "Resale tax document deleted successfully",
                type: "success",
                position: "top-right"
              });

              if (this.selectedDoc && this.selectedDoc.id === id) {
                this.closeFeedback();
              }
            } else {
              throw new Error("Delete failed");
            }
          })
          .catch(error => {
            console.error("Error deleting resale tax:", error);
            this.$toast.open({
              message: "Error deleting resale tax document",
              type: "error",
              position: "top-right"
            });
          });
      }
    },
    validateDocument(id) {
      if (!confirm("Are you sure you want to validate this resale tax?")) {
        return;
      }
      const notes = window.prompt(
        "Optional validation notes (leave empty to skip):",
        ""
      );
      validateResaleTaxApi(id, notes || undefined)
        .then(() => {
          this.$toast.open({
            message: "Resale tax validated successfully",
            type: "success",
            position: "top-right"
          });

          if (this.selectedDoc && this.selectedDoc.id === id) {
            this.closeFeedback();
          }

          // Reload documents to get updated data from server
          this.loadDocuments();
        })
        .catch(error => {
          console.error("Error validating resale tax:", error);
          this.$toast.open({
            message: "Error validating resale tax",
            type: "error",
            position: "top-right"
          });
        });
    },
    validateDocumentAI(id) {
      this.aiValidatingId = id;
      validateResaleTaxAIApi(id)
        .then(res => {
          const status = res && res.aiStatus ? res.aiStatus : "error";
          if (status === "rate_limited") {
            this.$toast.open({
              message: "Rate limit reached — wait a moment and try again.",
              type: "warning",
              position: "top-right"
            });
            return;
          }
          const type =
            status === "passed"
              ? "success"
              : status === "failed"
              ? "warning"
              : "error";
          this.$toast.open({
            message:
              status === "passed"
                ? "AI validated the document"
                : status === "failed"
                ? "AI rejected the document"
                : "AI validation could not complete",
            type,
            position: "top-right"
          });
          // Refresh the open modal with the updated document
          if (
            this.selectedDoc &&
            res &&
            res.data &&
            this.selectedDoc.id === res.data.id
          ) {
            this.selectedDoc = res.data;
          }
          this.loadDocuments();
        })
        .catch(error => {
          console.error("Error running AI validation:", error);
          this.$toast.open({
            message: "Error running AI validation",
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.aiValidatingId = null;
        });
    },
    validateAllPendingAI() {
      if (
        !confirm(
          "Run AI validation on all pending documents? This may take a while."
        )
      ) {
        return;
      }
      this.bulkValidating = true;
      validateAllPendingResaleTaxAIApi()
        .then(res => {
          const s = (res && res.summary) || {
            processed: 0,
            passed: 0,
            failed: 0,
            errors: 0
          };
          this.bulkResult = {
            summary: s,
            errors: (res && res.errors) || [],
            remaining: res && res.remaining ? res.remaining : 0,
            rateLimited: !!(res && res.rateLimited)
          };
          this.loadDocuments();
        })
        .catch(error => {
          console.error("Error running bulk AI validation:", error);
          this.$toast.open({
            message: "Error running bulk AI validation",
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.bulkValidating = false;
        });
    },
    isRejected(document) {
      // API returns rejected as a string ("0"/"1"); "0" is truthy in JS.
      return Number(document.rejected) === 1;
    },
    closeBulkResult() {
      this.bulkResult = null;
    },
    openFeedback(document) {
      this.selectedDoc = document;
    },
    openCustomer(document) {
      const route = this.$router.resolve({
        name: "InfoautoCustomersPage",
        params: {
          storeCode: this.$route.params.storeCode,
          departmentCode: this.$route.params.departmentCode
        },
        query: {
          search: document.customerEmail || document.customerName || "",
          ia_open: document.infoautoId
        }
      });
      window.open(route.href, "_blank");
    },
    closeFeedback() {
      this.selectedDoc = null;
    },
    isImageDoc(fileName) {
      const ext = (fileName || "")
        .split(".")
        .pop()
        .toLowerCase();
      return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
    },
    aiStatusLabel(status) {
      switch (status) {
        case "passed":
          return "Passed";
        case "failed":
          return "Failed";
        case "error":
          return "Error";
        default:
          return "Not run";
      }
    },
    aiStatusClass(status) {
      switch (status) {
        case "passed":
          return "bg-green-100 text-green-800";
        case "failed":
          return "bg-red-100 text-red-800";
        case "error":
          return "bg-gray-200 text-gray-700";
        default:
          return "bg-gray-100 text-gray-500";
      }
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.resale-tax-container {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  padding: 0;

  @media screen and (min-width: 768px) {
    padding: 0 1rem;
  }
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;

  /* Custom scrollbar for better UX */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;

    &:hover {
      background: #555;
    }
  }

  table {
    min-width: 100%;
    table-layout: auto;

    @media screen and (min-width: 1920px) {
      // On HD screens (1920px+), let table use full width without scroll
      width: 100%;
    }

    @media screen and (max-width: 1919px) {
      // Below HD, allow horizontal scroll if needed
      min-width: 1200px;
    }

    td,
    th {
      white-space: nowrap;
    }

    // Make email column slightly flexible if space allows
    td:nth-child(5),
    th:nth-child(5) {
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
<style lang="postcss">
#resale-tax-feedback-modal .modal-content {
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
}
#resale-tax-bulk-modal .modal-content {
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
}
</style>
